import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';

export class CloudsLayer extends BaseProduct {
  public readonly id: ProductId = 'clouds';
  private unsubscribeStore: (() => void) | null = null;
  private lastModel: string = '';
  private lastLevels: string = '';

  protected onActivate(): void {
    this.refresh();
    const s = store.getState();
    this.lastModel = s.selectedModels['clouds'] || s.cloudModel || 'ecmwf';
    this.lastLevels = JSON.stringify(s.cloudLevels || ['total']);
    this.unsubscribeStore = store.subscribe((newState) => {
      if (!this.active) return;
      const currentModel = newState.selectedModels['clouds'] || newState.cloudModel || 'ecmwf';
      const currentLevels = JSON.stringify(newState.cloudLevels || ['total']);
      if (currentModel !== this.lastModel || currentLevels !== this.lastLevels) {
        this.lastModel = currentModel;
        this.lastLevels = currentLevels;
        this.refresh();
      }
    });
  }

  protected onDeactivate(): void {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.lastModel = '';
    this.lastLevels = '';
    const state = store.getState();
    const manifest = state.manifest;
    const config = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__);
    const exposed: string[] = manifest?.exposed_models?.clouds || config?.exposed_models?.clouds || ['ecmwf', 'gfs', 'hrrr', 'rdps', 'rrfs'];
    const allModels = Array.from(new Set([...exposed, 'blend', 'ecmwf', 'gfs', 'hrrr', 'rdps', 'rrfs']));
    const allLevels = ['high', 'mid', 'low', 'total'];

    allModels.forEach(model => {
      allLevels.forEach(level => {
        this.layerManager.removeBufferedRasterLayer(`${this.id}-${model}-${level}`);
      });
    });
    // Legacy cleanup
    allLevels.forEach(level => {
      this.layerManager.removeBufferedRasterLayer(`${this.id}-${level}`);
    });
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(targetTimeMs?: number) {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const current = targetTimeMs !== undefined ? targetTimeMs : state.currentTimeStep;
    if (!current) return;

    const config = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__);
    const exposed: string[] = manifest?.exposed_models?.clouds || config?.exposed_models?.clouds || ['ecmwf', 'gfs', 'hrrr', 'rdps', 'rrfs'];
    const allKnownModels = Array.from(new Set([...exposed, 'ecmwf', 'gfs', 'hrrr', 'rdps', 'rrfs'])).filter(m => m !== 'blend');
    const allLevels = ['high', 'mid', 'low', 'total'];

    const selectedModel = state.selectedModels['clouds'] || state.cloudModel || 'ecmwf';
    const activeModels: string[] = (state.cloudModels && state.cloudModels.length > 1)
      ? state.cloudModels.filter((m: string) => m !== 'blend')
      : [selectedModel !== 'blend' ? selectedModel : 'ecmwf'];

    const activeLevels: string[] = state.cloudLevels || ['total'];

    allKnownModels.forEach(model => {
      const isModelActive = activeModels.includes(model);
      const modelData = manifest.clouds?.available_by_model?.[model] || manifest.clouds?.available?.[model] || {};

      allLevels.forEach(level => {
        const subLayerId = `${this.id}-${model}-${level}`;
        const isLevelActive = activeLevels.includes(level);
        const availableTimes = modelData[level] || [];

        if (isModelActive && isLevelActive && availableTimes.length > 0) {
          const ok = this.updateStandardInterpolatedRaster({
            layerId: subLayerId,
            product: this.id,
            model,
            targetTime: current,
            opacity,
            minzoom: 0,
            maxzoom: 5,
            formatHour: (dateStr, cleanHourStr) => `${level}_${dateStr}_${cleanHourStr}UTC`
          });
          if (!ok) {
            this.layerManager.removeBufferedRasterLayer(subLayerId);
          }
        } else {
          this.layerManager.removeBufferedRasterLayer(subLayerId);
        }
      });
    });
  }
}
export default CloudsLayer;
